import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';

// =========================================================================
// ContractMarkdown -- renders an operator-authored contract body (the
// `body_md` / `body_snapshot` markdown) as formatted, readable text.
//
// SAFETY: `react-markdown` is safe by default — it parses CommonMark and
// emits a sanitized React tree. We do NOT enable `rehype-raw`, so any raw
// HTML in the source is escaped, never executed. Links are gate-checked
// against an http(s)/mailto allow-list; images are suppressed (a legal
// document body should never hot-link remote images / tracking pixels).
//
// Unlike the shop's MarkdownDescription, headings here keep a real
// hierarchy because legal documents legitimately use section headings.
// =========================================================================

const SAFE_HREF_RE = /^(https?:|mailto:)/i;

function isSafeHref(href: string | null | undefined): boolean {
  if (!href) return false;
  return SAFE_HREF_RE.test(href.trim());
}

const components: Components = {
  p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="list-disc pl-5 my-2 space-y-1 first:mt-0 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 my-2 space-y-1 first:mt-0 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }) => (
    <h2 className="display text-lg text-ink mt-5 mb-2 first:mt-0">{children}</h2>
  ),
  h2: ({ children }) => (
    <h3 className="display text-base text-ink mt-4 mb-1.5 first:mt-0">{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className="font-semibold text-ink mt-4 mb-1 first:mt-0">{children}</h4>
  ),
  h4: ({ children }) => (
    <h5 className="font-semibold text-ink mt-3 mb-1 first:mt-0">{children}</h5>
  ),
  h5: ({ children }) => (
    <p className="font-semibold text-ink mt-3 mb-1 first:mt-0">{children}</p>
  ),
  h6: ({ children }) => (
    <p className="font-semibold text-ink mt-3 mb-1 first:mt-0">{children}</p>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-rule-strong pl-3 italic text-muted my-2 first:mt-0 last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="border-t border-rule-strong my-4" />,
  code: ({ children, className }) => (
    <code className={`bg-paper-2 px-1 py-0.5 rounded text-[12px] font-mono ${className ?? ''}`}>
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="bg-paper-2 p-2 rounded overflow-x-auto text-[12px] font-mono my-2 first:mt-0 last:mb-0">
      {children}
    </pre>
  ),
  img: () => null,
  a: ({ href, children }) => {
    if (!isSafeHref(href)) return <span>{children}</span>;
    return (
      <a
        href={href ?? undefined}
        target="_blank"
        rel="nofollow noopener noreferrer"
        className="text-clay underline hover:text-clay-deep"
      >
        {children}
      </a>
    );
  },
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="w-full border-collapse">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-rule-strong px-2 py-1 text-left font-semibold bg-paper-2">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-rule-strong px-2 py-1 align-top">{children}</td>
  ),
};

export interface ContractMarkdownProps {
  source: string | null | undefined;
  className?: string;
}

/** Render a contract body markdown string as formatted text. */
export function ContractMarkdown({ source, className }: ContractMarkdownProps) {
  const trimmed = (source ?? '').trim();
  if (!trimmed) return null;
  return (
    <div className={className}>
      <ReactMarkdown components={components}>{trimmed}</ReactMarkdown>
    </div>
  );
}
